/**
 * Extract weekly Keyfuels diesel price from a supplier PDF text layer (no OCR).
 */

import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  parseKeyfuelsDieselPriceLine,
  type KeyfuelsDieselPriceParse,
} from './keyfuelsFuelPriceParse';

export type { KeyfuelsDieselPriceParse } from './keyfuelsFuelPriceParse';
export {
  KEYFUELS_DIESEL_PRICE_RE,
  parseKeyfuelsDieselPriceLine,
  ukDateToYmd,
} from './keyfuelsFuelPriceParse';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

/** Extract concatenated text from every page of a PDF (text layer only). */
export async function extractTextFromPdf(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(buf),
    useWorkerFetch: false,
    isEvalSupported: false,
  });
  const pdf = await loadingTask.promise;
  const parts: string[] = [];

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    const pageText = (content.items as Array<{ str?: string }>)
      .map((item) => (typeof item.str === 'string' ? item.str : ''))
      .join(' ');
    parts.push(pageText);
  }

  return parts.join('\n');
}

/** Drop PDF → text → Keyfuels regex. Throws if the file is not a PDF or has no match. */
export async function parseKeyfuelsDieselPriceFromPdf(
  file: File,
): Promise<KeyfuelsDieselPriceParse> {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.pdf') && file.type !== 'application/pdf') {
    throw new Error('Weekly fuel price expects a PDF file.');
  }

  const text = await extractTextFromPdf(file);
  if (!text.trim()) {
    throw new Error(
      'This PDF has no extractable text. Export a text PDF from Keyfuels (not a scanned image).',
    );
  }

  const parsed = parseKeyfuelsDieselPriceLine(text);
  if (!parsed) {
    throw new Error(
      'Couldn’t find a Keyfuels diesel price line. Expected wording like: “Your Keyfuels Diesel fuel price for week commencing DD/MM/YY is NNN.NN pence per litre + VAT”.',
    );
  }
  return parsed;
}
